/**
 * Main App Component with StarNexus Routes
 * @module App
 *
 */

import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import BreadcrumbGroup from '@amzn/awsui-components-console/breadcrumb-group';
import { AppShell } from './components/starnexus/common/AppShell';
import { ErrorBoundary } from './components/starnexus/common/ErrorBoundary';
import { AuthGuard } from './components/starnexus/common/AuthGuard';
import { KeyboardShortcutsModal } from './components/starnexus/common/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from './hooks/starnexus/useKeyboardShortcuts';
import { useAuthStore } from './stores/starnexus/authStore';
import { UserRole, LegacyUserRole } from './types/starnexus/team';

// Eagerly loaded: home + dashboard + dataset list/detail are the landing surfaces and
// already part of the initial paint — code-splitting them only delays first paint.
import { HomePage } from './components/starnexus/home';
import { DashboardPage } from './components/starnexus/dashboard';
import { DatasetListPage } from './components/starnexus/datasets';
import { DatasetDetailPage } from './components/starnexus/datasets/DatasetDetailPage';
import { UploadSnapshotPage } from './components/starnexus/datasets/UploadSnapshotPage';

// Lazy-loaded: heavy or rarely-visited surfaces. The wizard pulls in DuckDB-WASM
// (~110 MB) plus ace-builds; monitoring/docs pages are admin-only or seldom hit.
// Splitting these out trims the initial JS bundle by hundreds of KB and keeps the
// DuckDB chunk off the home-page critical path.
const CreateDatasetWizard = React.lazy(() =>
  import('./components/starnexus/wizard').then(m => ({ default: m.CreateDatasetWizard })));
const ProcessingDashboard = React.lazy(() =>
  import('./components/starnexus/processing').then(m => ({ default: m.ProcessingDashboard })));
const TeamManagementPage = React.lazy(() =>
  import('./components/starnexus/team').then(m => ({ default: m.TeamManagementPage })));
const MonitoringDashboard = React.lazy(() =>
  import('./components/starnexus/monitoring').then(m => ({ default: m.MonitoringDashboard })));
const SettingsPage = React.lazy(() =>
  import('./components/starnexus/settings').then(m => ({ default: m.SettingsPage })));
const GettingStartedPage = React.lazy(() =>
  import('./components/starnexus/docs').then(m => ({ default: m.GettingStartedPage })));
const SchemaConfigPage = React.lazy(() =>
  import('./components/starnexus/docs').then(m => ({ default: m.SchemaConfigPage })));
const AndesIntegrationPage = React.lazy(() =>
  import('./components/starnexus/docs').then(m => ({ default: m.AndesIntegrationPage })));
const DatasetExplorerPage = React.lazy(() =>
  import('./components/starnexus/andes').then(m => ({ default: m.DatasetExplorerPage })));
const PermissionRequestsPage = React.lazy(() =>
  import('./components/starnexus/andes').then(m => ({ default: m.PermissionRequestsPage })));
const SubscriptionsPage = React.lazy(() =>
  import('./components/starnexus/andes').then(m => ({ default: m.SubscriptionsPage })));

// Placeholder pages
import { AccessDeniedPage } from './components/starnexus/pages/PlaceholderPages';

const RouteFallback: React.FC = () => (
  <div aria-busy="true" style={{ padding: 24 }}>Loading…</div>
);

// ============================================================================
// Route Guard Component
// ============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: (UserRole | LegacyUserRole)[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const role = useAuthStore((state) => state.role);
  const status = useAuthStore((state) => state.status);

  // Auth is still loading — render a quiet placeholder until ready.
  if (status !== 'ready') {
    return <div aria-busy="true" style={{ padding: 24 }}>Loading…</div>;
  }

  // If role-based access control is specified, check the user's role
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
};

// ============================================================================
// App Component
// ============================================================================

const App: React.FC = () => {
  // Initialize keyboard shortcuts
  const { shortcuts, helpModalVisible, hideHelpModal } = useKeyboardShortcuts();

  // Initialize auth store on mount — fetches whoami + teams/mine in parallel.
  useEffect(() => {
    void useAuthStore.getState().initialize();
  }, []);

  return (
    <>
      {/* Keyboard shortcuts help modal */}
      <KeyboardShortcutsModal visible={helpModalVisible} onDismiss={hideHelpModal} shortcuts={shortcuts} />

      <AuthGuard>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Redirect root to StarNexus dashboard */}
        <Route path="/" element={<Navigate to="/starnexus" replace />} />

        {/* StarNexus Home page with hero header */}
        <Route path="/starnexus" element={<HomePage />} />

        {/* Dashboard page */}
        <Route
          path="/starnexus/dashboard"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'Dashboard', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <DashboardPage />
            </AppShell>
          }
        />

        {/* StarUploader routes */}
        <Route
          path="/starnexus/uploader/datasets"
          element={
            <AppShell
              contentType="table"
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'StarUploader', href: '/starnexus/uploader/datasets' },
                    { text: 'Datasets', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <DatasetListPage />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/uploader/datasets/create"
          element={
            <AppShell
              contentType="wizard"
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'StarUploader', href: '/starnexus/uploader/datasets' },
                    { text: 'Datasets', href: '/starnexus/uploader/datasets' },
                    { text: 'Create dataset', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <CreateDatasetWizard />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/uploader/datasets/:datasetId/upload"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'StarUploader', href: '/starnexus/uploader/datasets' },
                    { text: 'Datasets', href: '/starnexus/uploader/datasets' },
                    { text: 'Upload snapshot', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <UploadSnapshotPage />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/uploader/datasets/:datasetId"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'StarUploader', href: '/starnexus/uploader/datasets' },
                    { text: 'Datasets', href: '/starnexus/uploader/datasets' },
                    { text: 'Dataset details', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <ErrorBoundary>
                <DatasetDetailPage />
              </ErrorBoundary>
            </AppShell>
          }
        />

        <Route
          path="/starnexus/uploader/processing"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'StarUploader', href: '/starnexus/uploader/datasets' },
                    { text: 'Processing', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <ProcessingDashboard />
            </AppShell>
          }
        />

        {/* Andes pages */}
        <Route
          path="/starnexus/andes/explorer"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'Andes', href: '#' },
                    { text: 'Dataset explorer', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <DatasetExplorerPage />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/andes/permissions"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'Andes Access', href: '#' },
                    { text: 'Permission requests', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <PermissionRequestsPage />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/andes/subscriptions"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'Andes', href: '#' },
                    { text: 'Subscriptions', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <SubscriptionsPage />
            </AppShell>
          }
        />

        {/* Documentation pages */}
        <Route
          path="/starnexus/docs/getting-started"
          element={
            <AppShell>
              <GettingStartedPage />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/docs/schema-config"
          element={
            <AppShell>
              <SchemaConfigPage />
            </AppShell>
          }
        />

        <Route
          path="/starnexus/docs/andes-integration"
          element={
            <AppShell>
              <AndesIntegrationPage />
            </AppShell>
          }
        />

        {/* Team Management - Owner only */}
        <Route
          path="/starnexus/team"
          element={
            <ProtectedRoute allowedRoles={['Owner', 'manager', 'admin']}>
              <AppShell>
                <TeamManagementPage />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Monitoring - Owner/Admin only */}
        <Route
          path="/starnexus/monitoring"
          element={
            <ProtectedRoute allowedRoles={['Owner', 'admin']}>
              <AppShell
                breadcrumbs={
                  <BreadcrumbGroup
                    items={[
                      { text: 'StarNexus', href: '/starnexus' },
                      { text: 'Monitoring', href: '#' }
                    ]}
                    ariaLabel="Breadcrumbs"
                  />
                }
              >
                <MonitoringDashboard />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/starnexus/settings"
          element={
            <AppShell
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: 'StarNexus', href: '/starnexus' },
                    { text: 'Settings', href: '#' }
                  ]}
                  ariaLabel="Breadcrumbs"
                />
              }
            >
              <SettingsPage />
            </AppShell>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/starnexus" replace />} />
      </Routes>
      </Suspense>
      </AuthGuard>
    </>
  );
};

export default App;
